import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Mascot } from "./Mascot";
import { Check, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export function SetupChecklist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dismissKey = `mg_checklist_dismissed:${user?.clinic_id || "no-clinic"}:${user?.user_id || "anonymous"}`;

  const load = () => {
    setLoading(true); setError("");
    api.get("/clinic/setup-checklist").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Unable to load your setup guide.")).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const d = localStorage.getItem(dismissKey);
    setDismissed(d === "1");
  }, [dismissKey]);

  if (dismissed) return null;
  if (loading) return <div className="mg-card mb-6 text-sm text-slate-500" data-testid="checklist-loading">Loading setup guide…</div>;
  if (error) return <div className="mg-card mb-6 border-red-200 bg-red-50" data-testid="checklist-error"><div className="font-semibold text-red-700">Setup guide unavailable</div><div className="text-sm text-red-600 mt-1">{error}</div><button onClick={load} className="mg-btn-secondary text-sm mt-3" data-testid="checklist-retry">Retry</button></div>;
  if (!data) return null;
  // Hide once fully complete (after a celebration moment is shown one time)
  const allDone = data.percent >= 100;

  if (!expanded) {
    return (
      <div className="relative mb-4 flex items-center gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-slate-50 to-emerald-50 px-4 py-3" data-testid="setup-checklist">
        <Mascot size={44} variant="main" className="drop-shadow-sm shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-extrabold text-mg-navy truncate">Clinic setup · {data.completed}/{data.total} complete</div>
            <button onClick={() => setExpanded(true)} className="text-xs text-mg-medical font-bold shrink-0" data-testid="checklist-toggle">Show steps</button>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-mg-medical to-mg-health" style={{ width: `${data.percent}%` }} /></div>
        </div>
        <button onClick={() => { setDismissed(true); localStorage.setItem(dismissKey, "1"); }} className="w-7 h-7 rounded-full hover:bg-white grid place-items-center text-slate-400 shrink-0" title="Dismiss" data-testid="checklist-dismiss"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-6 border border-blue-100"
      style={{
        backgroundImage: "linear-gradient(135deg, #F8FAFC 0%, #ECFDF5 100%)",
      }}
      data-testid="setup-checklist"
    >
      <button
        onClick={() => { setDismissed(true); localStorage.setItem(dismissKey, "1"); }}
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-slate-500"
        title="Dismiss"
        data-testid="checklist-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        {/* Left: mascot + progress */}
        <div className="p-6 flex items-center md:items-end md:justify-center bg-white/40">
          <div className="flex md:flex-col items-center gap-4 md:gap-2 w-full">
            <Mascot size={120} variant="main" className="mg-float drop-shadow-md" />
            <div className="md:text-center w-full">
              <div className="text-xs uppercase tracking-wider text-mg-medical font-bold">Setup</div>
              <div className="text-2xl font-extrabold text-mg-navy leading-tight">
                {data.completed}/{data.total}
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${data.percent}%`,
                    backgroundImage: "linear-gradient(135deg, #2090CF 0%, #66C32C 100%)",
                  }}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{data.percent}% complete</div>
            </div>
          </div>
        </div>

        {/* Right: steps */}
        <div className="p-6 pl-2 md:pl-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-extrabold text-mg-navy" data-testid="checklist-title">
                {allDone ? "You're all set! ✨" : "Let's get your clinic ready"}
              </h3>
              <p className="text-xs text-slate-600">
                {allDone
                  ? "Your clinic is fully configured. MediGenie is ready to greet your patients."
                  : "MediGenie has lined up a quick onboarding plan — finish these to start filling your calendar."}
              </p>
            </div>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-mg-medical font-semibold"
              data-testid="checklist-toggle"
            >
              {expanded ? "Hide" : "Show"}
            </button>
          </div>

          {expanded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.steps.map((s) => (
                <ChecklistItem key={s.id} step={s} />
              ))}
            </div>
          )}

          {allDone && (
            <button
              onClick={() => { setDismissed(true); localStorage.setItem(dismissKey, "1"); toast.success("Checklist tucked away — MediGenie is ready!"); }}
              className="mt-4 mg-btn-primary text-sm"
              data-testid="checklist-dismiss-done"
            >
              Hide checklist <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ step }) {
  const inner = (
    <div
      className={`relative flex items-start gap-3 p-3 rounded-xl border transition-all bg-white ${
        step.done
          ? "border-emerald-200"
          : "border-slate-100 hover:border-mg-medical hover:shadow-sm"
      }`}
      data-testid={`checklist-step-${step.id}`}
    >
      <div
        className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
          step.done ? "bg-mg-health text-white" : "bg-slate-100 text-slate-400 border border-slate-200"
        }`}
      >
        {step.done ? <Check className="w-3.5 h-3.5" /> : <span className="text-[10px] font-bold">·</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${step.done ? "text-slate-500 line-through" : "text-mg-navy"}`}>
          {step.title}
          {typeof step.count === "number" && step.count > 0 && (
            <span className="ml-1.5 text-[10px] bg-blue-50 text-mg-medical px-1.5 py-0.5 rounded-full font-bold">{step.count}</span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">{step.description}</div>
        {step.link && (
          <span className="text-[11px] text-mg-medical font-bold inline-flex items-center gap-1 mt-1.5">
            {step.done ? "Review" : step.cta} <ArrowRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </div>
  );

  if (step.link) {
    if (step.link.startsWith("/clinic/")) {
      return <a href={step.link} target="_blank" rel="noreferrer" className="block">{inner}</a>;
    }
    return <Link to={step.link} className="block">{inner}</Link>;
  }
  return inner;
}
